import React, { useState, Suspense, lazy, useRef, useCallback } from 'react';
import {
  IonIcon, IonButton, IonSpinner, IonActionSheet,
  IonPopover, IonList, IonItem, IonLabel, IonAlert,
  IonItemDivider, IonSelect, IonSelectOption,
} from '@ionic/react';
import {
  ellipsisVertical, enterOutline, exitOutline,
  trashOutline, pencilOutline, settingsOutline,
  expandOutline, colorPaletteOutline, squareOutline,
  scanOutline,
} from 'ionicons/icons';
import type { DashboardWidget } from '../services/dashboard';
import './WidgetRenderer.css';

// Lazy load widget components
const ClockWidget = lazy(() => import('./widgets/ClockWidget'));
const WeatherWidget = lazy(() => import('./widgets/WeatherWidget'));
const CalendarWidget = lazy(() => import('./widgets/CalendarWidget'));
const ChoresWidget = lazy(() => import('./widgets/ChoresWidget'));
const ChoresTodayWidget = lazy(() => import('./widgets/ChoresTodayWidget'));
const PhotoFrameWidget = lazy(() => import('./widgets/PhotoFrameWidget'));
const InspirationWidgetWrapper = lazy(() => import('./widgets/InspirationWidgetWrapper'));
const TravelWidget = lazy(() => import('./TravelWidget'));
const FamilyMembersWidget = lazy(() => import('./widgets/FamilyMembersWidget'));
const CountdownWidget = lazy(() => import('./widgets/CountdownWidget'));
const GroceryListWidget = lazy(() => import('./widgets/GroceryListWidget'));
const AnnouncementsWidget = lazy(() => import('./widgets/AnnouncementsWidget'));
const NotesWidget = lazy(() => import('./widgets/NotesWidget'));
const IframeWidget = lazy(() => import('./widgets/IframeWidget'));
const SectionWidget = lazy(() => import('./widgets/SectionWidget'));
const SpacerWidget = lazy(() => import('./widgets/SpacerWidget'));
const VisualScheduleWidget = lazy(() => import('./widgets/VisualScheduleWidget'));

interface SectionInfo {
  id: string;
  label: string;
}

interface WidgetRendererProps {
  widget: DashboardWidget;
  householdId: string;
  editMode?: boolean;
  onConfigure?: () => void;
  onConfigureWidget?: (widgetId: string) => void; // For configuring child widgets by ID
  onDelete?: () => void;
  onDeleteWidget?: (widgetId: string) => void; // For deleting child widgets by ID
  onUpdateWidget?: (widgetId: string, updates: Partial<DashboardWidget>) => void;
  // Section container props (legacy - kept for compatibility)
  sectionChildren?: DashboardWidget[];
  isExpanded?: boolean;
  onToggleCollapse?: (expanded: boolean) => void;
  isDragOver?: boolean;
  onMoveToSection?: (widgetId: string, sectionId: string | null, columnIndex?: number) => void;
  // Available sections for "Move to Section" action
  availableSections?: SectionInfo[];
  // All widgets in the dashboard (for sections to find their children)
  allWidgets?: DashboardWidget[];
  // dnd-kit props
  isDragging?: boolean;
  isInSection?: boolean; // Whether this widget is inside a section column
  // Show section as potential drop target when something is being dragged
  isPotentialDropTarget?: boolean;
  // Sortable listeners for child widgets (legacy, kept for compatibility)
  sortableListeners?: Record<string, unknown>;
  // Drag listeners from SortableWidget
  dragListeners?: Record<string, unknown>;
  // Fullscreen mode props
  isFullscreen?: boolean; // Whether widget is currently in fullscreen overlay
  onExpand?: (widgetId: string) => void; // Callback to expand widget to fullscreen
}

const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  widget,
  householdId,
  editMode = false,
  onConfigure,
  onConfigureWidget,
  onDelete,
  onDeleteWidget,
  onUpdateWidget,
  sectionChildren = [],
  isExpanded = true,
  onToggleCollapse,
  isDragOver = false,
  onMoveToSection,
  availableSections = [],
  allWidgets = [],
  isDragging = false,
  isInSection = false,
  isPotentialDropTarget = false,
  sortableListeners,
  dragListeners,
  isFullscreen = false,
  onExpand,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [showMoveSheet, setShowMoveSheet] = useState(false);
  const [showSizeSheet, setShowSizeSheet] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [popoverEvent, setPopoverEvent] = useState<any>(null);
  const [showRenameAlert, setShowRenameAlert] = useState(false);

  // Widget type checks
  const isSection = widget.widget_type === 'section';
  const isSpacer = widget.widget_type === 'spacer';
  // Use prop if provided, otherwise derive from widget data
  const widgetIsInSection = isInSection || !!widget.parent_widget_id;
  const canDrag = editMode && !isSection && !isSpacer;
  // Can this widget be expanded to fullscreen? (all widgets except layout widgets)
  const canExpand = !isSection && !isSpacer && onExpand && !isFullscreen;

  // Combine refs for sections (both draggable and droppable aren't needed, just droppable)
  const widgetRef = useRef<HTMLDivElement>(null);

  // Resize state for child widgets
  const resizeRef = useRef<{ startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);

  // Handle resize start for child widgets inside sections (corner resize - width & height)
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!widgetRef.current) return;

    const parentElement = widgetRef.current.parentElement;
    if (!parentElement) return;

    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: parentElement.offsetWidth,
      startHeight: parentElement.offsetHeight,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizeRef.current || !widgetRef.current) return;

      const parentElement = widgetRef.current.parentElement;
      const sectionContainer = parentElement?.parentElement;
      if (!parentElement || !sectionContainer) return;

      // Width resize
      const deltaX = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = resizeRef.current.startWidth + deltaX;
      const containerWidth = sectionContainer.offsetWidth;
      const newGridW = Math.max(2, Math.min(12, Math.round((newWidth / containerWidth) * 12)));
      const newWidthPercent = (newGridW / 12) * 100;
      parentElement.style.width = `${newWidthPercent}%`;

      // Height resize
      const deltaY = moveEvent.clientY - resizeRef.current.startY;
      const newHeight = Math.max(100, resizeRef.current.startHeight + deltaY);
      parentElement.style.height = `${newHeight}px`;
    };

    const handleMouseUp = () => {
      if (!resizeRef.current || !widgetRef.current) return;

      const parentElement = widgetRef.current.parentElement;
      const sectionContainer = parentElement?.parentElement;
      if (!parentElement || !sectionContainer) return;

      const containerWidth = sectionContainer.offsetWidth;
      const finalWidth = parentElement.offsetWidth;
      const finalHeight = parentElement.offsetHeight;
      const newGridW = Math.max(2, Math.min(12, Math.round((finalWidth / containerWidth) * 12)));

      // Calculate grid_h based on a ~60px row height
      const rowHeight = 60;
      const newGridH = Math.max(2, Math.round(finalHeight / rowHeight));

      // Update the widget with new dimensions
      const updates: Partial<DashboardWidget> = {};
      if (newGridW !== widget.grid_w) updates.grid_w = newGridW;
      if (newGridH !== widget.grid_h) updates.grid_h = newGridH;

      if (Object.keys(updates).length > 0 && onUpdateWidget) {
        onUpdateWidget(widget.id, updates);
      }

      // Reset inline height style (let CSS handle it based on grid_h)
      parentElement.style.height = '';

      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [widget.id, widget.grid_w, widget.grid_h, onUpdateWidget]);

  // canMove derived from widget type checks above
  const canMove = !isSection && !isSpacer;

  // Handle duplicate widget
  const handleDuplicate = () => {
    setShowPopover(false);
    // Duplicate by creating a new widget with same config
    // This would need an onDuplicate callback from parent
    if (onUpdateWidget) {
      // Signal parent to duplicate - we'll pass a special update
      // For now, just log it - parent needs to implement duplicate
      console.log('Duplicate widget:', widget.id);
    }
  };

  // Handle size change
  const handleSizeChange = (newWidth: number, newHeight?: number) => {
    setShowSizeSheet(false);
    if (onUpdateWidget) {
      const updates: Partial<DashboardWidget> = { grid_w: newWidth };
      if (newHeight) updates.grid_h = newHeight;
      onUpdateWidget(widget.id, updates);
    }
  };

  // Handle quick style updates
  const handleBackgroundChange = (color: string) => {
    if (onUpdateWidget) {
      onUpdateWidget(widget.id, { background_color: color || undefined });
    }
  };

  const handleBorderRadiusChange = (radius: string) => {
    if (onUpdateWidget) {
      const numRadius = parseInt(radius, 10);
      if (!isNaN(numRadius)) {
        onUpdateWidget(widget.id, { border_radius: numRadius });
      }
    }
  };

  // Handle rename for sections - show alert
  const handleRename = () => {
    setShowPopover(false);
    setShowRenameAlert(true);
  };

  // Confirm rename
  const confirmRename = (newLabel: string) => {
    const currentLabel = (widget.config?.label as string) || 'Section';
    if (newLabel && newLabel !== currentLabel && onUpdateWidget) {
      onUpdateWidget(widget.id, {
        config: { ...widget.config, label: newLabel },
      });
    }
  };

  // Open popover
  const openPopover = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPopoverEvent(e.nativeEvent);
    setShowPopover(true);
  };

  const renderWidget = () => {
    const props = {
      householdId,
      config: widget.config,
    };

    switch (widget.widget_type) {
      case 'clock':
        return <ClockWidget {...props} />;
      
      case 'weather':
        return <WeatherWidget {...props} />;
      
      case 'calendar':
        // Unified calendar widget - viewType comes from config
        return <CalendarWidget {...props} />;

      // Legacy calendar widget types - kept for backwards compatibility with existing dashboards
      // These are hidden from new widget selection but still render for existing widgets
      case 'agenda':
      case 'calendar_day':
      case 'calendar_week': {
        const viewTypeMap: Record<string, 'agenda' | 'day' | 'week'> = {
          'agenda': 'agenda',
          'calendar_day': 'day',
          'calendar_week': 'week',
        };
        return (
          <CalendarWidget
            householdId={householdId}
            config={{ ...widget.config, viewType: viewTypeMap[widget.widget_type] }}
          />
        );
      }
      
      case 'chores':
        return <ChoresWidget householdId={householdId} config={widget.config} />;

      // Legacy chores widgets - kept for backwards compatibility
      case 'chores_today':
      case 'chores_board':
        return <ChoresTodayWidget {...props} viewType={widget.widget_type} />;
      
      case 'photo_frame':
        return <PhotoFrameWidget {...props} />;
      
      case 'inspiration':
        return <InspirationWidgetWrapper {...props} />;
      
      case 'travel_times':
      case 'commute':
        return <TravelWidget householdId={householdId} hoursAhead={props.config?.hoursAhead as number} />;
      
      case 'family_members':
      case 'whos_home':
      case 'birthdays':
        return <FamilyMembersWidget {...props} viewType={widget.widget_type} />;
      
      case 'countdown':
        return <CountdownWidget {...props} />;
      
      case 'grocery_list':
        return <GroceryListWidget {...props} />;
      
      case 'announcements':
        return <AnnouncementsWidget {...props} />;
      
      case 'notes':
        return <NotesWidget {...props} />;
      
      case 'iframe':
        return <IframeWidget {...props} />;

      // Layout widgets
      // Note: SectionWidget is typically rendered directly by DashboardDisplay now,
      // but we keep this case for backwards compatibility and simple section display
      case 'section':
        // SectionWidget now handles its own children via allWidgets prop
        // This case is only used when WidgetRenderer renders a section directly
        return (
          <SectionWidget
            widget={widget}
            householdId={householdId}
            editMode={editMode}
            allWidgets={allWidgets}
            isExpanded={isExpanded}
            onToggleCollapse={onToggleCollapse}
            isDragOver={isDragOver}
            isPotentialDropTarget={isPotentialDropTarget}
            onUpdateWidget={onUpdateWidget}
            onDeleteWidget={onDeleteWidget}
            onConfigureWidget={onConfigureWidget}
            onMoveToSection={onMoveToSection}
            availableSections={availableSections}
          />
        );

      case 'spacer':
        return <SpacerWidget {...props} editMode={editMode} />;

      case 'visual_schedule':
        return (
          <VisualScheduleWidget
            householdId={householdId}
            config={widget.config}
          />
        );

      // Smart home widgets would require Home Assistant setup
      case 'ha_controls':
      case 'ha_climate':
      case 'ha_cameras':
        return (
          <div className="widget-placeholder">
            <span>🏠</span>
            <p>Home Assistant widget</p>
            <small>Configure Home Assistant in settings</small>
          </div>
        );
      
      default:
        return (
          <div className="widget-placeholder">
            <span>📦</span>
            <p>Unknown widget: {widget.widget_type}</p>
          </div>
        );
    }
  };

  // Layout widgets (section, spacer) should be transparent
  const isLayoutWidget = ['section', 'spacer'].includes(widget.widget_type);

  const widgetStyle: React.CSSProperties = {
    backgroundColor: isLayoutWidget ? 'transparent' : (widget.background_color || 'var(--ion-card-background)'),
    borderRadius: `${widget.border_radius ?? 12}px`,
  };

  // Build action sheet buttons for moving to sections
  const moveSheetButtons = [
    // Option to remove from section (if in one)
    ...(widgetIsInSection ? [{
      text: 'Remove from Section',
      icon: exitOutline,
      handler: () => {
        onMoveToSection?.(widget.id, null);
      },
    }] : []),
    // Options to move to each available section
    ...availableSections
      .filter(s => s.id !== widget.parent_widget_id) // Don't show current section
      .map(section => ({
        text: `Move to "${section.label}"`,
        icon: enterOutline,
        handler: () => {
          onMoveToSection?.(widget.id, section.id);
        },
      })),
    {
      text: 'Cancel',
      role: 'cancel' as const,
    },
  ];

  // Build class names
  const classNames = [
    'widget',
    editMode ? 'editing' : '',
    widgetIsInSection ? 'in-section' : '',
    isDragging ? 'is-dragging' : '',
    isDragOver ? 'drag-over' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={widgetRef}
      className={classNames}
      style={widgetStyle}
    >
      {/* Edit mode controls - entire bar is draggable except menu button */}
      {editMode && (
        <div
          className="widget-edit-controls"
          {...(dragListeners || sortableListeners || {})}
        >
          <div className="widget-edit-label">
            {widget.title || widget.widget_type}
          </div>
          <div
            className="widget-edit-actions"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Three-dot menu */}
            <IonButton fill="clear" size="small" onClick={openPopover}>
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
          </div>
        </div>
      )}

      {/* Resize handle for widgets inside sections */}
      {editMode && widgetIsInSection && (
        <div
          className="widget-resize-handle"
          onMouseDown={handleResizeStart}
          title="Drag to resize"
        />
      )}

      {/* Popover menu */}
      <IonPopover
        isOpen={showPopover}
        event={popoverEvent}
        onDidDismiss={() => setShowPopover(false)}
        className="widget-options-popover"
      >
        <IonList lines="none" className="widget-options-list">
          {/* Rename (sections only) */}
          {isSection && onUpdateWidget && (
            <IonItem button onClick={handleRename}>
              <IonIcon icon={pencilOutline} slot="start" />
              <IonLabel>Rename</IonLabel>
            </IonItem>
          )}

          {/* Size options (non-section widgets) */}
          {!isSection && onUpdateWidget && (
            <IonItem button onClick={() => { setShowPopover(false); setShowSizeSheet(true); }}>
              <IonIcon icon={expandOutline} slot="start" />
              <IonLabel>Resize</IonLabel>
            </IonItem>
          )}

          {/* Quick style options for non-layout widgets */}
          {!isSection && !isSpacer && onUpdateWidget && (
            <>
              <IonItemDivider className="widget-menu-divider" />

              {/* Background Color */}
              <IonItem className="widget-menu-item">
                <IonIcon icon={colorPaletteOutline} slot="start" />
                <IonLabel>Background</IonLabel>
                <IonSelect
                  value={widget.background_color || 'default'}
                  onIonChange={(e) => handleBackgroundChange(e.detail.value === 'default' ? '' : e.detail.value)}
                  interface="popover"
                  className="widget-style-select"
                >
                  <IonSelectOption value="default">Default</IonSelectOption>
                  <IonSelectOption value="transparent">Transparent</IonSelectOption>
                  <IonSelectOption value="#1e293b">Dark</IonSelectOption>
                  <IonSelectOption value="#0f172a">Darker</IonSelectOption>
                  <IonSelectOption value="#ffffff">White</IonSelectOption>
                  <IonSelectOption value="#f1f5f9">Light Gray</IonSelectOption>
                  <IonSelectOption value="#3b82f6">Blue</IonSelectOption>
                  <IonSelectOption value="#10b981">Green</IonSelectOption>
                  <IonSelectOption value="#f59e0b">Amber</IonSelectOption>
                  <IonSelectOption value="#ef4444">Red</IonSelectOption>
                  <IonSelectOption value="#8b5cf6">Purple</IonSelectOption>
                </IonSelect>
              </IonItem>

              {/* Border Radius */}
              <IonItem className="widget-menu-item">
                <IonIcon icon={squareOutline} slot="start" />
                <IonLabel>Corners</IonLabel>
                <IonSelect
                  value={String(widget.border_radius ?? 12)}
                  onIonChange={(e) => handleBorderRadiusChange(e.detail.value)}
                  interface="popover"
                  className="widget-style-select"
                >
                  <IonSelectOption value="0">Square</IonSelectOption>
                  <IonSelectOption value="4">Slight</IonSelectOption>
                  <IonSelectOption value="8">Small</IonSelectOption>
                  <IonSelectOption value="12">Medium</IonSelectOption>
                  <IonSelectOption value="16">Large</IonSelectOption>
                  <IonSelectOption value="24">Extra Large</IonSelectOption>
                </IonSelect>
              </IonItem>
            </>
          )}

          <IonItemDivider className="widget-menu-divider" />

          {/* Move to Section (non-layout widgets only) */}
          {canMove && availableSections.length > 0 && onMoveToSection && (
            <IonItem button onClick={() => { setShowPopover(false); setShowMoveSheet(true); }}>
              <IonIcon icon={widgetIsInSection ? exitOutline : enterOutline} slot="start" />
              <IonLabel>Move to Section...</IonLabel>
            </IonItem>
          )}

          {/* Remove from section (if in one) */}
          {widgetIsInSection && onMoveToSection && (
            <IonItem button onClick={() => { setShowPopover(false); onMoveToSection(widget.id, null); }}>
              <IonIcon icon={exitOutline} slot="start" />
              <IonLabel>Remove from Section</IonLabel>
            </IonItem>
          )}

          {/* Configure (if available) */}
          {onConfigure && (
            <IonItem button onClick={() => { setShowPopover(false); onConfigure(); }}>
              <IonIcon icon={settingsOutline} slot="start" />
              <IonLabel>More Settings...</IonLabel>
            </IonItem>
          )}

          <IonItemDivider className="widget-menu-divider" />

          {/* Delete */}
          {onDelete && (
            <IonItem button onClick={() => { setShowPopover(false); onDelete(); }} className="danger-item">
              <IonIcon icon={trashOutline} slot="start" color="danger" />
              <IonLabel color="danger">Delete</IonLabel>
            </IonItem>
          )}
        </IonList>
      </IonPopover>

      {/* Action sheet for moving to sections */}
      <IonActionSheet
        isOpen={showMoveSheet}
        onDidDismiss={() => setShowMoveSheet(false)}
        header="Move Widget"
        buttons={moveSheetButtons}
      />

      {/* Action sheet for resizing */}
      <IonActionSheet
        isOpen={showSizeSheet}
        onDidDismiss={() => setShowSizeSheet(false)}
        header="Widget Size"
        subHeader={`Current: ${widget.grid_w}/12 columns`}
        buttons={[
          {
            text: 'Small (3 cols)',
            handler: () => handleSizeChange(3),
          },
          {
            text: 'Medium (4 cols)',
            handler: () => handleSizeChange(4),
          },
          {
            text: 'Half (6 cols)',
            handler: () => handleSizeChange(6),
          },
          {
            text: 'Large (8 cols)',
            handler: () => handleSizeChange(8),
          },
          {
            text: 'Full Width (12 cols)',
            handler: () => handleSizeChange(12),
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />

      {/* Rename section alert */}
      <IonAlert
        isOpen={showRenameAlert}
        onDidDismiss={() => setShowRenameAlert(false)}
        header="Rename Section"
        inputs={[
          {
            name: 'sectionName',
            type: 'text',
            placeholder: 'Section name',
            value: (widget.config?.label as string) || 'Section',
          },
        ]}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Rename',
            handler: (data) => {
              confirmRename(data.sectionName);
            },
          },
        ]}
      />

      {/* Expand to fullscreen button (view mode only, not for layout widgets) */}
      {canExpand && !editMode && (
        <button
          className="widget-expand-button"
          onClick={() => onExpand(widget.id)}
          aria-label="Expand to fullscreen"
          title="Expand to fullscreen"
        >
          <IonIcon icon={scanOutline} />
        </button>
      )}

      {/* Widget title */}
      {widget.show_title && widget.title && (
        <div className="widget-title">{widget.title}</div>
      )}

      {/* Widget content */}
      <div className={`widget-content ${isFullscreen ? 'fullscreen' : ''}`}>
        {error ? (
          <div className="widget-error">
            <span>⚠️</span>
            <p>{error}</p>
          </div>
        ) : (
          <Suspense fallback={
            <div className="widget-loading">
              <IonSpinner />
            </div>
          }>
            {renderWidget()}
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default WidgetRenderer;
